/**
 * A cor da marca vira uma paleta (F169).
 *
 * ## Por que a loja escolhe UMA cor
 *
 * Um editor de tema completo entregaria ao lojista a chance de pintar
 * "Cancelado" de verde. A semântica de cor do produto é uma afirmação —
 * `docs/UI_UX.md`: verde não é "bom", é "terminou" (F156) —, e ela não é do
 * cliente. O que é dele é a identidade: o azul do Aeris some, a cor dele
 * aparece.
 *
 * ## O que é derivado, e por quê
 *
 * `hover` e `active` são a mesma cor mais escura, porque é o que os tokens do
 * produto já faziam com o azul da Aeris. `subtle` é o fundo de destaque, muito
 * claro, e **não escurece no tema escuro**: quem cuida disso é o par de regras
 * que o `BrandingProvider` escreve.
 *
 * O FUNDO DO MENU é derivado escurecendo até quase preto, e não é a cor
 * escolhida: o texto e a logo ali são brancos, e uma loja que escolhesse
 * amarelo teria um menu ilegível. A cor da marca continua aparecendo — nos
 * botões, nos links, no item ativo do menu.
 */

type Rgb = { r: number; g: number; b: number }

const parse = (hex: string): Rgb | null => {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null
  return {
    r: Number.parseInt(hex.slice(1, 3), 16),
    g: Number.parseInt(hex.slice(3, 5), 16),
    b: Number.parseInt(hex.slice(5, 7), 16),
  }
}

const toHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b]
    .map((channel) =>
      Math.max(0, Math.min(255, Math.round(channel)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`

const mix = (color: Rgb, target: Rgb, amount: number): Rgb => ({
  r: color.r + (target.r - color.r) * amount,
  g: color.g + (target.g - color.g) * amount,
  b: color.b + (target.b - color.b) * amount,
})

const BLACK: Rgb = { r: 0, g: 0, b: 0 }
const WHITE: Rgb = { r: 255, g: 255, b: 255 }

/**
 * Luminância relativa da WCAG — a definição, não uma aproximação.
 *
 * A média dos canais é a simplificação que faz amarelo e azul parecerem
 * igualmente claros, e é justamente entre esses dois que a decisão importa
 * aqui.
 */
const luminance = ({ r, g, b }: Rgb): number => {
  const channel = (value: number): number => {
    const v = value / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Razão de contraste da WCAG entre duas cores. */
export const contrastRatio = (a: string, b: string): number | null => {
  const left = parse(a)
  const right = parse(b)
  if (left === null || right === null) return null

  const bright = Math.max(luminance(left), luminance(right))
  const dark = Math.min(luminance(left), luminance(right))
  return (bright + 0.05) / (dark + 0.05)
}

/**
 * A cor serve para um botão com texto branco?
 *
 * 4,5:1 é o mínimo da WCAG para texto normal, e é o piso que `tokens.css` já
 * anota em cada cor crua da Aeris. Recusar aqui é o que impede uma loja de
 * escolher amarelo-claro e descobrir depois que ninguém lê os botões dela.
 */
export const READABLE_ON_WHITE_TEXT = 4.5

export const isReadableWithWhiteText = (hex: string): boolean => {
  const ratio = contrastRatio(hex, '#ffffff')
  return ratio !== null && ratio >= READABLE_ON_WHITE_TEXT
}

export type BrandPalette = {
  readonly primary: string
  readonly primaryHover: string
  readonly primaryActive: string
  readonly primarySubtle: string
  readonly sidebarBg: string
}

/** Null quando o texto não é uma cor de seis dígitos. */
export const paletteFrom = (hex: string): BrandPalette | null => {
  const base = parse(hex)
  if (base === null) return null

  return {
    primary: toHex(base),
    primaryHover: toHex(mix(base, BLACK, 0.14)),
    primaryActive: toHex(mix(base, BLACK, 0.26)),
    primarySubtle: toHex(mix(base, WHITE, 0.88)),
    /*
     * Quase preto com um toque da marca. O menu tem texto e logo brancos, e é
     * por isso que ele não pode ser a cor escolhida: a identidade aparece no
     * item ativo e nos botões, que é onde ela é lida como decisão de marca e
     * não como acidente.
     */
    sidebarBg: toHex(mix(base, BLACK, 0.82)),
  }
}
